// Service catalog for cad4surveys (mirrors backend list). Kept client-side
// for instant navigation; dynamic fetch also supported via /api/services.
export const SERVICES = [
  {
    slug: "boundary-survey",
    title: "Boundary Survey",
    short:
      "Definitive determination of property lines and corners, drafted to local jurisdiction standards.",
    description:
      "We produce precise boundary survey CAD drawings that establish legal property lines, corners, monuments, and easements. Our drafters cross-reference record descriptions with measured field data to resolve senior rights, gaps, and overlaps in accordance with state-specific standards of practice.",
    deliverables: [
      "Signed-ready CAD (.dwg) boundary map",
      "Layered plat with bearings, distances, curves",
      "Corner monument table and legend",
      "Closure report and PDF exhibit",
    ],
    documents: [
      "Field notes / digital field book",
      "Point files (.txt, .csv) with PNEZD format",
      "Raw data files (.rw5, .raw, .tds, .fbk)",
      "Existing deeds & legal descriptions (subject + adjoiners)",
      "Recorded plats and subdivision maps",
      "Title commitment / title search report",
      "Prior boundary surveys (if available)",
      "Tax map / parcel map reference",
    ],
  },
  {
    slug: "alta-nsps-land-title-survey",
    title: "ALTA / NSPS Land Title Survey",
    short:
      "Full compliance with 2021 ALTA/NSPS Minimum Standard Detail Requirements for commercial real estate.",
    description:
      "We deliver ALTA/NSPS survey CAD drawings tailored to Table A items requested by your client, lender, or title company. Every easement, encroachment, and record exception is mapped and labeled for commercial due diligence.",
    deliverables: [
      "ALTA-compliant .dwg with Table A items",
      "Signed surveyor certification block",
      "Schedule B exceptions plotted and labeled",
      "PDF exhibit with north arrow, legend, title block",
    ],
    documents: [
      "Title commitment with Schedule A & B",
      "All Schedule B-II exception documents (easements, covenants)",
      "Field notes and topographic data",
      "Point files (.csv, .txt) and raw data (.rw5)",
      "Zoning report / zoning letter",
      "Client-specified Table A item list",
      "Recorded plats, deeds, and prior ALTA surveys",
      "Flood zone / FEMA FIRM reference",
    ],
  },
  {
    slug: "topographic-survey",
    title: "Topographic Survey",
    short:
      "Detailed existing-conditions drafting with contours, spot elevations, and planimetric features.",
    description:
      "We convert your raw topographic field data and point clouds into engineering-ready base maps with accurate contours, breaklines, and surface features — ready for civil design, site planning, or permit submittal.",
    deliverables: [
      "Layered CAD topographic base map",
      "Contours (minor/major) at required interval",
      "TIN / DTM surface file",
      "Planimetric feature layers (utilities, vegetation, hardscape)",
    ],
    documents: [
      "Point files (.csv, .txt) with descriptors",
      "Raw data (.rw5, .tds, .fbk)",
      "Drone / LiDAR point cloud (.las, .laz, .e57)",
      "Field sketches and photographs",
      "Utility locate reports (811 tickets)",
      "Benchmark datum and coordinate system",
      "Client scope / required contour interval",
    ],
  },
  {
    slug: "subdivision-platting",
    title: "Subdivision Platting",
    short:
      "Preliminary and final subdivision plat drafting compliant with city and county regulations.",
    description:
      "We draft subdivision plats, lot layouts, easements, dedications, and signature blocks ready for municipal review. Our team is fluent in jurisdiction-specific checklists across the USA.",
    deliverables: [
      "Preliminary and final plat .dwg",
      "Lot & block layout with dimensions",
      "Easement, ROW, and dedication notes",
      "Signature and approval blocks",
    ],
    documents: [
      "Boundary survey CAD file",
      "Parent parcel deed & legal description",
      "Engineering site plan / master plan",
      "City/County subdivision regulations checklist",
      "Utility and drainage easement proposals",
      "Title commitment",
      "HOA / covenant documents (if any)",
    ],
  },
  {
    slug: "as-built-survey",
    title: "As-Built Survey",
    short:
      "Post-construction verification drawings comparing field conditions to design documents.",
    description:
      "We draft as-built CAD deliverables that document constructed improvements — utilities, structures, grading — versus approved design plans, for municipal acceptance, record drawings, and facility management.",
    deliverables: [
      "As-built overlay .dwg vs. design",
      "Invert elevations, rim elevations, pipe sizes",
      "Deviation report / redlines",
      "PDF record drawings set",
    ],
    documents: [
      "Approved construction plans (.dwg and .pdf)",
      "Field-measured point files and raw data",
      "Utility invert / rim measurements",
      "Contractor submittals & change orders",
      "Pipe material, size, and slope data",
      "Photographs of constructed features",
    ],
  },
  {
    slug: "construction-staking-drafting",
    title: "Construction Staking Drafting",
    short:
      "Stakeout point preparation and cut sheets from design files for field crews.",
    description:
      "We prepare stakeout coordinate files, cut sheets, and offset drawings that your field crews upload straight to total stations, GPS rovers, and robotic instruments.",
    deliverables: [
      "Stakeout point file (.csv, .txt) with PNEZD + descriptors",
      "Cut sheets with station/offset/elevation",
      "Annotated stakeout plan .dwg",
      "Instrument-ready .job / .dc files",
    ],
    documents: [
      "Approved civil design drawings (.dwg)",
      "Horizontal and vertical alignments",
      "Grading plan / proposed surface",
      "Control points and benchmarks",
      "Client preferred instrument format (Trimble, Topcon, Leica)",
    ],
  },
  {
    slug: "route-right-of-way-survey",
    title: "Route / Right-of-Way Survey",
    short:
      "Linear corridor drafting for highways, pipelines, transmission lines, and rail.",
    description:
      "We draft route surveys and ROW exhibits for linear infrastructure with stationing, alignment sheets, parcel impact exhibits, and ROW acquisition plans.",
    deliverables: [
      "Alignment sheets with stationing",
      "Parcel impact maps and take/remainder areas",
      "ROW exhibits with legal descriptions",
      "Strip topo with centerline CAD",
    ],
    documents: [
      "Centerline alignment (coordinates or .xml)",
      "Field topographic data + point files",
      "Parcel ownership data / GIS layer",
      "Title search for each impacted parcel",
      "Existing ROW / easement documents",
      "Agency-specific template (DOT, utility)",
    ],
  },
  {
    slug: "fema-elevation-certificate",
    title: "FEMA Elevation Certificate Drafting",
    short:
      "FEMA Form 086-0-33 preparation with building diagrams and photographs.",
    description:
      "We prepare FEMA Elevation Certificates with accurate building diagrams, LAG/HAG elevations, and photograph plates — ready for surveyor signature and insurance submittal.",
    deliverables: [
      "Completed FEMA EC (Form 086-0-33)",
      "Building diagram selection with measurements",
      "Front and rear photograph plates",
      "Location map with FIRM panel reference",
    ],
    documents: [
      "Field-measured elevations (LAG, HAG, lowest floor)",
      "Photographs of front, rear, and machinery",
      "Building address, FIRM panel, community ID",
      "Benchmark and datum reference (NAVD88)",
      "Lowest adjacent grade point file",
      "Client/owner information form",
    ],
  },
  {
    slug: "hydrographic-bathymetric-drafting",
    title: "Hydrographic / Bathymetric Drafting",
    short:
      "Underwater topographic drafting with sounding data for lakes, ponds, rivers, and harbors.",
    description:
      "We process sounding data and multibeam sonar files into bathymetric CAD surfaces and contour maps for dredging volume, reservoir capacity, and waterway design.",
    deliverables: [
      "Bathymetric contour .dwg",
      "3D surface (.xml, .tin)",
      "Volumetric analysis (if requested)",
      "Cross-sections at requested stations",
    ],
    documents: [
      "Sounding data (.xyz, .csv) with tide correction",
      "Multibeam sonar raw files (.s7k, .gsf)",
      "Water surface elevation / tide gauge data",
      "Shoreline boundary CAD",
      "Datum reference (MLLW, NAVD88)",
    ],
  },
  {
    slug: "mortgage-location-plot-plan",
    title: "Mortgage Location / Plot Plan",
    short:
      "Quick-turn plot plans for residential lenders, refinances, and zoning submittals.",
    description:
      "We deliver residential plot plans showing structures, setbacks, easements, and encroachments from recorded plat and field measurements — typically within 24–48 hours.",
    deliverables: [
      "Plot plan PDF with structures and setbacks",
      "Easements and encroachments labeled",
      "Setback dimensions to all property lines",
      "Signature-ready title block",
    ],
    documents: [
      "Recorded plat of subdivision",
      "Deed / legal description",
      "Field measurements (point file or sketch)",
      "Client form with owner & lender info",
      "Photographs of structures (optional)",
    ],
  },
  {
    slug: "volumetric-earthwork",
    title: "Volumetric / Earthwork Drafting",
    short:
      "Cut/fill analysis, stockpile volumes, and earthwork quantity reports.",
    description:
      "We generate volumetric reports for stockpiles, borrow pits, and grading projects from drone, LiDAR, or conventional survey data — with documented methodology.",
    deliverables: [
      "Cut / fill color map .dwg",
      "Volumetric report PDF",
      "TIN surface comparison files",
      "Cross-sections at stations",
    ],
    documents: [
      "Pre- and post-condition point files",
      "Drone orthomosaic / point cloud (.las)",
      "Design surface (.xml)",
      "Boundary of stockpile or work area",
      "Density / shrinkage factor (if applicable)",
    ],
  },
  {
    slug: "legal-description-exhibits",
    title: "Legal Description & Exhibits",
    short:
      "Metes-and-bounds writing and exhibit drafting for easements, dedications, and conveyances.",
    description:
      "We write metes-and-bounds legal descriptions with matching exhibits that pass surveyor review and recording office scrutiny — for easements, lot splits, and conveyances.",
    deliverables: [
      "Metes-and-bounds legal description (Word)",
      "Matching exhibit CAD & PDF",
      "Closure calculation report",
      "Surveyor signature block",
    ],
    documents: [
      "Boundary or parcel CAD file",
      "Deed or plat reference",
      "Proposed easement / area centerline",
      "Jurisdiction recording requirements",
      "Signed client scope",
    ],
  },
  {
    slug: "gis-drafting",
    title: "GIS Drafting & Conversion",
    short: "CAD ↔ GIS conversion, data cleanup, attribution, and map drafting.",
    description:
      "We convert between CAD and GIS (ArcGIS, QGIS), clean attribute tables, build geodatabases, and produce thematic maps for municipalities and utilities.",
    deliverables: [
      "ESRI Geodatabase (.gdb) or Shapefiles",
      "Clean AutoCAD .dwg from GIS layers",
      "Attribute table with QA",
      "Thematic map .pdf",
    ],
    documents: [
      "Source GIS files (.shp, .gdb, .kml)",
      "CAD files to convert (.dwg)",
      "Coordinate system / projection",
      "Attribute schema / data dictionary",
      "Sample map layout / template",
    ],
  },
];

export const SERVICE_MAP = Object.fromEntries(SERVICES.map((s) => [s.slug, s]));

// Per-survey-type process snippet + figure shown on the service detail page.
// Figures are stable ("delivered" totals), shown over a 50-state grid.
export const SERVICE_META = {
  "boundary-survey": {
    heading: "Boundary Surveys, drafted to your jurisdiction.",
    intro: "Property-line work where every bearing, monument and call has to hold up under recording-office scrutiny.",
    process: [
      { label: "Records research", body: "We pull deeds, recorded plats, and adjoiner descriptions and resolve senior rights, gaps & overlaps before the pencil hits the drawing." },
      { label: "Closure & layering", body: "Field point files & raw data are reduced, closure run, and the drawing built on your template — bearings, distances, curves, monuments, and easements." },
      { label: "Plat + closure report", body: "You receive a signed-ready .dwg, PDF exhibit, monument legend, and closure report — drafted to your state's minimum standards." },
    ],
    figure: { value: "1,847", unit: "boundary plats", caption: "Drafted across 41 states" },
  },
  "alta-nsps-land-title-survey": {
    heading: "ALTA / NSPS Land Title Surveys for due diligence.",
    intro: "2021 ALTA/NSPS-compliant deliverables tailored to the Table A items your title company or lender requires.",
    process: [
      { label: "Schedule B-II review", body: "Every recorded easement, covenant and exception is read, mapped, and labeled — nothing left on the cutting-room floor." },
      { label: "Drafted to Table A", body: "Drawing is built to your specified Table A items, with surveyor certification block ready for stamp." },
      { label: "Title-ready exhibit", body: "Final .dwg + PDF passes title-company QA on first submission, with all encroachments and overlaps clearly called out." },
    ],
    figure: { value: "612", unit: "ALTA jobs", caption: "Across all 50 states" },
  },
  "topographic-survey": {
    heading: "Topographic Surveys — design-ready base maps.",
    intro: "We turn raw points and point clouds into clean contours and planimetric layers your civil team can design over.",
    process: [
      { label: "Data reduction", body: "Point files (.csv/.txt), raw data (.rw5/.tds), and LiDAR/drone clouds (.las/.laz) are processed and quality-checked." },
      { label: "Surface & breaklines", body: "We build TIN/DTM surfaces, generate contours at your interval, and add breaklines along edge-of-pavement, swales, and tops/toes." },
      { label: "Layered base map", body: "Drawing is delivered with utilities, vegetation, hardscape and structures on your standard layers — drop-in ready for design." },
    ],
    figure: { value: "2,310", unit: "topo base maps", caption: "From 12 raw-data formats" },
  },
  "subdivision-platting": {
    heading: "Subdivision Platting that survives city review.",
    intro: "Preliminary and final plats compliant with the local jurisdiction's checklist — ready for staff sign-off.",
    process: [
      { label: "Layout & dedications", body: "Lots, blocks, easements and ROW are drawn from your engineer's master plan, with dedications drafted as required." },
      { label: "Jurisdiction checklist", body: "Each city/county checklist is run line-by-line — signature blocks, north arrow, vicinity map, certifications, all included." },
      { label: "Submittal-ready set", body: "Preliminary plat, final plat, exhibits, and required attachments delivered as one tidy package." },
    ],
    figure: { value: "486", unit: "plats recorded", caption: "Across 28 states" },
  },
  "as-built-survey": {
    heading: "As-Built Surveys — design vs. constructed.",
    intro: "Record drawings showing what was actually built versus the approved plan, ready for municipal acceptance.",
    process: [
      { label: "Field measurement", body: "We process your field-measured invert/rim elevations, pipe sizes, structure locations and grading data." },
      { label: "Overlay & deviations", body: "An as-built overlay is drafted against the design .dwg with deviations highlighted and a redline report." },
      { label: "Record set", body: "Final .dwg + PDF record set with all utilities, structures and grading documented to municipal standards." },
    ],
    figure: { value: "1,124", unit: "as-builts", caption: "From 200+ contractors" },
  },
  "construction-staking-drafting": {
    heading: "Construction Staking — instrument-ready files.",
    intro: "Stakeout points and cut sheets pulled directly from design files, formatted for your field crews.",
    process: [
      { label: "Alignment & grading", body: "Horizontal and vertical alignments, grading surfaces and control points are imported from your civil .dwg." },
      { label: "Cut sheets", body: "Stationing, offsets and cut/fill values are computed and exported to PNEZD with descriptors." },
      { label: "Instrument format", body: "Files exported native to Trimble, Topcon or Leica — your crew uploads and stakes, no conversion." },
    ],
    figure: { value: "94k+", unit: "stakeout points", caption: "Issued nationwide" },
  },
  "route-right-of-way-survey": {
    heading: "Route & Right-of-Way Surveys for linear corridors.",
    intro: "Stationed alignments, parcel impacts, and ROW exhibits for highways, pipelines, transmission lines, and rail.",
    process: [
      { label: "Centerline & strip topo", body: "Centerline alignment is built and the strip topo developed at your stationing interval." },
      { label: "Parcel impact mapping", body: "Each affected parcel is pulled from county GIS / title and mapped with take, remainder and access rights." },
      { label: "Acquisition exhibits", body: "Per-parcel ROW exhibits and legal descriptions delivered ready for agency review." },
    ],
    figure: { value: "318 mi", unit: "corridor drafted", caption: "DOT, utility & rail" },
  },
  "fema-elevation-certificate": {
    heading: "FEMA Elevation Certificates, drafted for stamp.",
    intro: "Form 086-0-33 prepared with accurate building diagrams, elevations and photographs — ready for surveyor signature.",
    process: [
      { label: "Form completion", body: "Field-measured LAG/HAG/lowest-floor elevations entered against the correct building diagram." },
      { label: "Photo plates", body: "Front and rear photographs assembled with the location map, FIRM panel and community ID." },
      { label: "Signature-ready PDF", body: "Final EC packet returned for your stamp and direct submittal to lender or insurance carrier." },
    ],
    figure: { value: "2,047", unit: "ECs prepared", caption: "Coastal & inland flood zones" },
  },
  "hydrographic-bathymetric-drafting": {
    heading: "Hydrographic & Bathymetric Drafting underwater.",
    intro: "Sounding data and multibeam sonar processed into bathymetric surfaces, contours and volumes.",
    process: [
      { label: "Sounding ingest", body: "Tide-corrected XYZ data (and multibeam .s7k/.gsf raw files) are processed against your datum." },
      { label: "Surface & contours", body: "Bathymetric TIN built, contours generated, and 3D surface exported for further analysis." },
      { label: "Volume & cross-sections", body: "Optional volumetric analysis (dredging / capacity) and cross-sections at any station." },
    ],
    figure: { value: "73", unit: "waterbodies", caption: "Lakes, rivers, harbors" },
  },
  "mortgage-location-plot-plan": {
    heading: "Mortgage / Plot Plans — 24–48 hour turnaround.",
    intro: "Residential plot plans showing structures, setbacks and easements — fast enough for closing schedules.",
    process: [
      { label: "Record & field", body: "Recorded plat, deed, and field measurement (or sketch) compiled into the working file." },
      { label: "Setbacks & encroachments", body: "Setback dimensions to all property lines, easements and any encroachments labeled clearly." },
      { label: "Lender-ready PDF", body: "Plot plan PDF with title block delivered same-day to next-day for refi or closing." },
    ],
    figure: { value: "5,860+", unit: "plot plans", caption: "Most in &lt;48 hours" },
  },
  "volumetric-earthwork": {
    heading: "Volumetric & Earthwork — cut/fill you can defend.",
    intro: "Stockpile, borrow-pit and grading-project volumes from drone, LiDAR or conventional survey data — with a documented method.",
    process: [
      { label: "Pre / post surfaces", body: "Pre- and post-condition point files or point clouds are processed into matched TIN surfaces." },
      { label: "Cut / fill compute", body: "Volumes calculated against your design surface (or pre-state), with a documented methodology in the report." },
      { label: "Color map & report", body: "Color cut/fill map .dwg + PDF report with stations, cross-sections and a clear basis-of-volume statement." },
    ],
    figure: { value: "1.4M cy", unit: "volume reported", caption: "Mining, civil & landfill" },
  },
  "legal-description-exhibits": {
    heading: "Legal Descriptions & Exhibits, ready to record.",
    intro: "Metes-and-bounds writing and matching exhibits for easements, dedications, and conveyances.",
    process: [
      { label: "Source CAD review", body: "Your boundary or parcel CAD file is closure-checked before any description is written." },
      { label: "Metes & bounds", body: "Description written in plain, recordable language — bearings, distances, curves, ties to monuments, point-of-beginning." },
      { label: "Matching exhibit", body: "Exhibit .dwg + PDF drafted to match the description, with closure calc report and surveyor signature block." },
    ],
    figure: { value: "3,142", unit: "legals written", caption: "Recorded across 36 states" },
  },
  "gis-drafting": {
    heading: "GIS Drafting & Conversion — clean, attributed data.",
    intro: "CAD ↔ GIS conversion, attribute cleanup, geodatabase construction, and thematic map drafting.",
    process: [
      { label: "Source assessment", body: "We review source SHP/GDB/KML or .dwg, your projection, and your data dictionary." },
      { label: "Conversion & QA", body: "Data is converted, attribute table cleaned, topology fixed, and projections set per your standard." },
      { label: "Map / GDB delivery", body: "Final .gdb or shapefiles + clean .dwg + thematic map PDFs delivered with a QA summary." },
    ],
    figure: { value: "210k+", unit: "features cleaned", caption: "Municipal & utility datasets" },
  },
};

// Tiny stable random fallback in case meta is missing
export function getServiceMeta(slug) {
  return SERVICE_META[slug] || {
    heading: "Land Surveying CAD Drafting",
    intro: "We draft to your standard, your jurisdiction, and your stamp.",
    process: [
      { label: "Intake", body: "Send us field data, records, and your template — we confirm scope within hours." },
      { label: "Drafting & QA", body: "Surveyor-trained drafters build the deliverable; an independent reviewer audits before delivery." },
      { label: "Delivery", body: "You receive .dwg + PDF + supporting files, with revision rounds included." },
    ],
    figure: { value: "10k+", unit: "drawings", caption: "Across 50 states" },
  };
}

export const BRAND = {
  name: "cad4surveys",
  tagline: "Land Surveying CAD Drafting — USA, All 50 States",
  email: "cad4surveysusa@gmail.com",
  phone_display: "+91 80836 91588",
  phone_e164: "+918083691588",
  whatsapp_url: "https://wa.me/918083691588",
  tel_url: "tel:+918083691588",
};
